import * as React from 'react';
import { Formatters, Messages } from '../../../gen-src/Localize';
import { autoFocus } from '../../utils/AutoFocus';
import { getDateWithMonthOffset } from '../../utils/Date';
import { generateId } from '../../utils/DomUtil';
import { CarouselWidget } from './CarouselWidget';
import { DatePickerBody } from './DatePickerBody';
import { DatePickerDayOfWeekHeader } from './DatePickerDayOfWeekHeader';
import { datePickerHeaderStyle, datePickerWidgetStyle } from './DatePickerStyles';

// FIXME the picker shouldn't actually be aware of SPACE vs ENTER, that is a concern of the dropdown
export interface DatePickerProps {
  /**
   * Autofocus the selected date cell when mounted.
   */
  autofocus?: boolean;
  /**
   * If defined, dates that return true are not selectable and display a disabled style.
   */
  isDateDisabled?: (date: Date) => boolean;
  /**
   * Method that is called with the Date object when the selected date is changed -- i.e. by clicking or pressing SPACE.
   */
  onChange?: (date: Date) => void;
  /**
   * Method that is called with the Date object when the selected date is confirmed -- i.e. by pressing ENTER.
   */
  onConfirm?: (date: Date) => void;
  /**
   * The current selected date.
   */
  selectedDate?: Date;
  /**
   * Displays an extra column showing ISO-Weeks. This results in the displayed week
   * starting on Monday. Defaults to false.
   */
  showWeekNumbers?: boolean;
  /**
   * Test id to be attached to the DatePicker widget.
   */
  testId?: string;
}

export interface DatePickerState {
  focusedDate: Date;
}

export class DatePickerWidget extends React.PureComponent<DatePickerProps, DatePickerState> {
  private readonly headerId: string = generateId();
  private gridDomRef: React.RefObject<HTMLDivElement> = React.createRef();
  public state: DatePickerState = {
    focusedDate: this.props.selectedDate || new Date(),
  };

  public componentDidMount(): void {
    if (this.props.autofocus) {
      autoFocus(false, this.gridDomRef.current!, false);
    }
  }

  public componentDidUpdate(prevProps: DatePickerProps): void {
    if (prevProps.selectedDate !== this.props.selectedDate) {
      this.setState({ focusedDate: this.props.selectedDate || new Date() });
    }
  }

  private handleOnConfirm = (date: Date): void => {
    this.props.onConfirm?.(date);
  }

  private handleOnChange = (date: Date): void => {
    if (this.props.onChange && date.getTime() !== this.props.selectedDate?.getTime()) {
      this.props.onChange(date);
    }
  }

  private handleFocusedDateChange = (date: Date): void => {
    this.setState({ focusedDate: date });
  }

  private handleClickPrevMonth = () => {
    this.handleFocusedDateChange(getDateWithMonthOffset(this.state.focusedDate, -1));
  }

  private handleClickNextMonth = () => {
    this.handleFocusedDateChange(getDateWithMonthOffset(this.state.focusedDate, 1));
  }

  private getDisplayedMonthAndYear(): string {
    return Formatters.formatFullMonthAndFullYear(this.state.focusedDate);
  }

  public render(): JSX.Element {
    return <div style={datePickerWidgetStyle} data-tb-test-id={this.props.testId}>
      <CarouselWidget
        id={this.headerId}
        nextButtonTitle={Messages.nextMonth()}
        onNextClick={this.handleClickNextMonth}
        onPreviousClick={this.handleClickPrevMonth}
        prevButtonTitle={Messages.previousMonth()}
        role='heading'
        style={datePickerHeaderStyle}
      >
        {this.getDisplayedMonthAndYear()}
      </CarouselWidget>
      <div aria-labelledby={this.headerId} role='grid' ref={this.gridDomRef} >
        <DatePickerDayOfWeekHeader showWeekNumbers={this.props.showWeekNumbers} />
        <DatePickerBody
          focusedDate={this.state.focusedDate}
          isDateDisabled={this.props.isDateDisabled}
          onChange={this.handleOnChange}
          onConfirm={this.handleOnConfirm}
          onFocusedDateChange={this.handleFocusedDateChange}
          selectedDate={this.props.selectedDate}
          showWeekNumbers={!!this.props.showWeekNumbers}
        />
      </div>
    </div>;
  }
}
